import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { CirclePlus, Send, Check, X, ChevronDown, Loader2 } from "lucide-react";
import { MachineMark } from "@/components/maintenance/MachineMark";
import { categoryLabel, formatINR } from "@/components/maintenance/lib";

// "There's a new machine on the floor" — the ERP's client-request flow
// (components/ClientRequestModals.js), in Maintenance's vocabulary. Anyone who
// can see the module can ask; every active admin is @mentioned; approving
// creates the machine. The form asks for everything RockLink needs to run a
// machine properly, so an approval is one click and not an interview.

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const L = "block text-[10px] font-bold uppercase tracking-[0.09em] text-[hsl(var(--muted-foreground))] mb-1.5";
const I = "w-full h-9 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 text-[13px] outline-none focus:border-[hsl(var(--primary))]/60";

const BLANK = {
  machine_no: "", label: "", make: "", model: "", category: "",
  area: "main_shed", kind: "lathe", serial_no: "", year_installed: "",
  downtime_rate_inr: "", pm_interval_days: "", note: "",
};

// The shape the API wants — empty strings become nulls, not zeroes.
const toPayload = (f) => ({
  ...f,
  downtime_rate_inr: String(f.downtime_rate_inr).trim() === "" ? null : Number(f.downtime_rate_inr),
  pm_interval_days: String(f.pm_interval_days).trim() === "" ? null : Number(f.pm_interval_days),
});

function useFormVocab(open) {
  const [v, setV] = useState(null);
  useEffect(() => {
    if (!open || v) return;
    axios.get(`${API}/maintenance/machine-requests/form`).then((r) => setV(r.data)).catch(() => {});
  }, [open, v]);
  return v;
}

/* The whole machine, in one form. Shared by the request dialog and the admin's
   approve-with-corrections dialog, so both validate identically. */
function MachineFields({ f, set, vocab }) {
  const upd = (k) => (e) => set({ ...f, [k]: e.target.value });
  return (
    <>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block">
          <span className={L}>ME code <span className="text-[hsl(var(--primary))]">required</span></span>
          <input value={f.machine_no} onChange={upd("machine_no")} placeholder="ME303018"
            data-testid="mr-machine-no" className={`${I} font-mono-data uppercase`} />
        </label>
        <label className="block">
          <span className={L}>What the floor calls it <span className="text-[hsl(var(--primary))]">required</span></span>
          <input value={f.label} onChange={upd("label")} placeholder="PUMA 800XLY II"
            data-testid="mr-label" className={I} />
        </label>
        <label className="block">
          <span className={L}>Make</span>
          <input value={f.make} onChange={upd("make")} list="mr-makes" placeholder="DOOSAN"
            data-testid="mr-make" className={`${I} uppercase`} />
          <datalist id="mr-makes">
            {(vocab?.makes || []).map((m) => <option key={m} value={m} />)}
          </datalist>
        </label>
        <label className="block">
          <span className={L}>Model</span>
          <input value={f.model} onChange={upd("model")} placeholder="PUMA 800XLY"
            data-testid="mr-model" className={I} />
        </label>
        <label className="block">
          <span className={L}>Category</span>
          <select value={f.category} onChange={upd("category")} data-testid="mr-category" className={I}>
            <option value="">Not sure</option>
            {(vocab?.categories || []).map((c) => <option key={c} value={c}>{categoryLabel(c)}</option>)}
          </select>
        </label>
        <label className="block">
          <span className={L}>What kind of machine</span>
          <select value={f.kind} onChange={upd("kind")} data-testid="mr-kind" className={I}>
            {(vocab?.kinds || []).map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
          </select>
        </label>
        <label className="block">
          <span className={L}>Where it stands</span>
          <select value={f.area} onChange={upd("area")} data-testid="mr-area" className={I}>
            {(vocab?.areas || []).map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
          </select>
        </label>
        <label className="block">
          <span className={L}>Serial number <span className="font-medium normal-case tracking-normal">(optional)</span></span>
          <input value={f.serial_no} onChange={upd("serial_no")} placeholder="From the OEM plate"
            data-testid="mr-serial" className={I} />
        </label>
        <label className="block">
          <span className={L}>Year it came in <span className="font-medium normal-case tracking-normal">(optional)</span></span>
          <input value={f.year_installed} onChange={upd("year_installed")} inputMode="numeric" placeholder="2026"
            data-testid="mr-year" className={`${I} font-mono-data`} />
        </label>
        <label className="block">
          <span className={L}>₹/hr when it's down <span className="font-medium normal-case tracking-normal">(optional)</span></span>
          <input value={f.downtime_rate_inr} onChange={upd("downtime_rate_inr")} inputMode="decimal"
            placeholder="An admin can set this later" data-testid="mr-rate" className={`${I} font-mono-data`} />
        </label>
      </div>
      <label className="block">
        <span className={L}>Service every … days <span className="font-medium normal-case tracking-normal">(optional — {vocab?.default_interval_days ?? 365} by default)</span></span>
        <input value={f.pm_interval_days} onChange={upd("pm_interval_days")} inputMode="numeric"
          placeholder={String(vocab?.default_interval_days ?? 365)} data-testid="mr-interval" className={`${I} font-mono-data`} />
      </label>
      <label className="block">
        <span className={L}>Anything the admin should know <span className="font-medium normal-case tracking-normal">(optional)</span></span>
        <input value={f.note} onChange={upd("note")} placeholder="Installed next to the second-shed door last week…"
          data-testid="mr-note" className={I} />
      </label>
    </>
  );
}

export function RequestMachineDialog({ open, onOpenChange, onSent }) {
  const [f, setF] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const vocab = useFormVocab(open);
  useEffect(() => { if (open) setF(BLANK); }, [open]);

  const submit = async () => {
    if (!f.machine_no.trim() || !f.label.trim() || busy) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/maintenance/machine-requests`, toPayload(f));
      const n = r.data?.admins_notified ?? 0;
      toast.success("Request sent", {
        description: n
          ? `${n} admin${n === 1 ? "" : "s"} notified — the machine appears once it's approved.`
          : "It's queued for an admin to approve.",
      });
      onOpenChange(false);
      onSent?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send that request.");
    } finally { setBusy(false); }
  };

  const ready = f.machine_no.trim() && f.label.trim();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl rl-frost max-h-[88vh] overflow-y-auto" data-testid="mt-machine-request-dialog">
        <DialogHeader>
          <DialogTitle className="text-[17px]">Request a new machine</DialogTitle>
          <DialogDescription className="text-[12px]">
            Fill in what you know — an admin checks it and adds the machine. It then works
            everywhere: breakdowns, preventive maintenance, downtime and cost.
          </DialogDescription>
        </DialogHeader>
        <MachineFields f={f} set={setF} vocab={vocab} />
        <div className="flex items-center justify-end gap-2 pt-1">
          <button onClick={() => onOpenChange(false)}
            className="rl-pill h-9 px-4 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[12px] font-semibold">
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={!ready || busy}
            title={ready ? undefined : "The ME code and the name are the two we can't guess"}
            data-testid="mr-submit"
            className="rl-lit-btn inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-[hsl(var(--primary))] text-white text-[12px] font-bold disabled:opacity-45 disabled:cursor-not-allowed"
          >
            <Send className="w-3.5 h-3.5" /> {busy ? "Sending…" : "Send to the admins"}
          </button>
        </div>
        <p className="text-[10.5px] text-[hsl(var(--muted-foreground))]">
          Requested by you automatically — no name to type.
        </p>
      </DialogContent>
    </Dialog>
  );
}

/* Admin corner — the pending asks, with the whole form open for correction
   before approval. Collapsed like the other admin cards. */
export default function MachineRequestsPanel({ pendingCount = 0, onChanged }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);      // {id, fields}
  const [rejecting, setRejecting] = useState(null);  // request row
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const vocab = useFormVocab(open);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/maintenance/machine-requests?status=pending`);
      setRows(r.data?.requests || []);
    } catch { setRows([]); }
  }, []);
  useEffect(() => { if (open) load(); }, [open, load]);

  const approve = async () => {
    if (!editing || busy) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/maintenance/machine-requests/${editing.id}/approve`, toPayload(editing.fields));
      toast.success(`${r.data?.machine?.label} added to the registry`);
      setEditing(null);
      await load(); onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add that machine.");
    } finally { setBusy(false); }
  };

  const reject = async () => {
    if (!rejecting || !reason.trim() || busy) return;
    setBusy(true);
    try {
      await axios.post(`${API}/maintenance/machine-requests/${rejecting.id}/reject`, { reason: reason.trim() });
      toast.success("Declined — the person who asked has been told");
      setRejecting(null); setReason("");
      await load(); onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "That didn't go through.");
    } finally { setBusy(false); }
  };

  const count = rows ? rows.length : pendingCount;

  return (
    <>
      <div className="rl-glass-edge rounded-[14px] bg-[hsl(var(--card))] mt-4 overflow-hidden" data-testid="mt-machine-requests-panel">
        <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center gap-2 px-[18px] py-3.5 text-left"
                data-testid="mt-machine-requests-toggle">
          <CirclePlus className="w-3.5 h-3.5 text-[hsl(var(--primary))]" />
          <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-[hsl(var(--foreground))]/85">New machine requests</span>
          {count > 0 ? (
            <span className="rl-extra-count is-alert">{count}</span>
          ) : (
            <span className="text-[11px] text-[hsl(var(--muted-foreground))]">nothing waiting</span>
          )}
          <ChevronDown className={`w-4 h-4 ml-auto text-[hsl(var(--muted-foreground))] transition-transform ${open ? "rotate-180" : ""}`} />
        </button>

        {open && (
          <div className="border-t border-[hsl(var(--border))]">
            {rows === null && <p className="px-[18px] py-5 text-[12px] text-[hsl(var(--muted-foreground))]">Loading…</p>}
            {rows && rows.length === 0 && (
              <p className="px-[18px] py-6 text-[12.5px] text-[hsl(var(--muted-foreground))] leading-relaxed max-w-[520px]">
                When a machine arrives on the floor, anyone in Maintenance can ask for it here —
                you check the details and add it. Nothing is waiting right now.
              </p>
            )}
            {(rows || []).map((r) => (
              <div key={r.id} className="px-[18px] py-3.5 border-t border-[hsl(var(--border))]/50 first:border-t-0"
                   data-testid={`mt-machine-request-${r.id}`}>
                <div className="flex items-start gap-3 flex-wrap">
                  <MachineMark make={r.make} size={28} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[14px] font-bold">{r.label}</span>
                      <span className="rl-me-chip rl-carved">{r.machine_no}</span>
                    </div>
                    <div className="text-[11.5px] text-[hsl(var(--muted-foreground))] mt-1">
                      {[r.make, r.model, categoryLabel(r.category), r.area === "second_shed" ? "Second shed" : "Main shed",
                        r.year_installed && `installed ${r.year_installed}`,
                        r.serial_no && `serial ${r.serial_no}`,
                        r.downtime_rate_inr != null && `${formatINR(r.downtime_rate_inr)}/hr`,
                        `service every ${r.pm_interval_days} days`].filter(Boolean).join(" · ")}
                    </div>
                    {r.note && <div className="text-[12px] mt-1">“{r.note}”</div>}
                    <div className="text-[10.5px] text-[hsl(var(--muted-foreground))] mt-1">
                      asked by {r.requested_by?.name || "—"}{r.requested_by_role ? ` · ${r.requested_by_role}` : ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setEditing({ id: r.id, fields: { ...BLANK, ...r,
                        downtime_rate_inr: r.downtime_rate_inr ?? "", pm_interval_days: r.pm_interval_days ?? "" } })}
                      data-testid={`mt-machine-approve-${r.id}`}
                      className="rl-lit-btn inline-flex items-center gap-1.5 h-8 px-3.5 rounded-full bg-green-600 hover:bg-green-500 text-white text-[12px] font-bold">
                      <Check className="w-3.5 h-3.5" /> Review &amp; add
                    </button>
                    <button onClick={() => { setRejecting(r); setReason(""); }}
                      data-testid={`mt-machine-reject-${r.id}`}
                      className="rl-pill inline-flex items-center gap-1.5 h-8 px-3.5 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[12px] font-semibold">
                      <X className="w-3.5 h-3.5" /> Decline
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-2xl rl-frost max-h-[88vh] overflow-y-auto" data-testid="mt-machine-approve-dialog">
          <DialogHeader>
            <DialogTitle className="text-[17px]">Add this machine</DialogTitle>
            <DialogDescription className="text-[12px]">
              Correct anything that was guessed, then add it. It joins the fleet immediately —
              breakdowns, preventive maintenance and cost all start working. Its tile on the
              shop-floor drawing is a separate layout job.
            </DialogDescription>
          </DialogHeader>
          {editing && <MachineFields f={editing.fields} set={(fields) => setEditing({ ...editing, fields })} vocab={vocab} />}
          <div className="flex items-center justify-end gap-2 pt-1">
            <button onClick={() => setEditing(null)}
              className="rl-pill h-9 px-4 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[12px] font-semibold">
              Cancel
            </button>
            <button onClick={approve} disabled={busy} data-testid="mt-machine-approve-confirm"
              className="rl-lit-btn inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-green-600 hover:bg-green-500 text-white text-[12px] font-bold disabled:opacity-50">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
              {busy ? "Adding…" : "Add to the registry"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!rejecting} onOpenChange={(v) => !v && setRejecting(null)}>
        <DialogContent className="max-w-md rl-frost" data-testid="mt-machine-reject-dialog">
          <DialogHeader>
            <DialogTitle className="text-[17px]">Decline this request</DialogTitle>
            <DialogDescription className="text-[12px]">
              {rejecting?.label} ({rejecting?.machine_no}) — the person who asked gets your reason.
            </DialogDescription>
          </DialogHeader>
          <label className="block">
            <span className={L}>Why? <span className="text-[hsl(var(--primary))]">required</span></span>
            <input value={reason} onChange={(e) => setReason(e.target.value)} autoFocus
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); reject(); } }}
              placeholder="It's the same machine as ME303011, under a new name…"
              data-testid="mt-machine-reject-reason" className={I} />
          </label>
          <div className="flex items-center justify-end gap-2 pt-1">
            <button onClick={() => setRejecting(null)}
              className="rl-pill h-9 px-4 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[12px] font-semibold">
              Cancel
            </button>
            <button onClick={reject} disabled={!reason.trim() || busy} data-testid="mt-machine-reject-confirm"
              className="rl-lit-btn h-9 px-4 rounded-full bg-slate-600 hover:bg-slate-500 text-white text-[12px] font-bold disabled:opacity-45 disabled:cursor-not-allowed">
              {busy ? "Saving…" : "Decline"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
